import { Build, Component, Element, Host, Prop, State, Watch, Listen, h } from '@stencil/core';
import debounce from '../../capabilities/debounce';
import uid from '../../capabilities/identity/uid';
import warn from '../../capabilities/warn';

/**
 * hp6-text-select
 *
 * @description The text-select decorates a <select />, applies hp-themes styling, and changes the width of the select as the selected option changes
 * @use
 * @since 5.11.0
 * @slot - `default slot`: The insertion of the select element.
 * @version figma-v1
 */
@Component({
  tag: 'hp6-text-select',
  styleUrl: 'text-select.scss',
  shadow: false, // shadow: false so that the <label /> can be properly associated with the <select />
  // Note that this does satisfy the single-tag policy since the <slot /> is a direct child of the <hp6-text-select />
})
export class TextSelect {
  /**
   * The label for this component. REQUIRED - Renders null if not supplied.
   */
  @Prop() labelText!: string;

  /**
   * The size of the label text. "xsmall Heading" or "small Heading"
   * - xxsmall heading label uses xsmall body for selection options
   * - xsmall heading label uses small body for selection options
   * - small heading label uses medium body for selection options
   * - medium heading label uses large body for selection options
   */
  @Prop() labelSizeSelection: 'xxs' | 'xs' | 'sm' | 'md' = 'xs';

  /**
   * ref to the <Host /> element
   */
  @Element() host: HTMLElement;

  /**
   * The read/write selected index of the wrapped select.
   * Use this prop to change selectElement.selectedOption instead of setting selected="true" on an <option />
   * or using defaultValue or value on the <select /> to avoid browser warnings.
   */
  @Prop({ mutable: true, reflect: true }) selectedIndex: number = 0;

  /**
   * isLoaded indicates whether the component has been loaded
   */
  @State() isLoaded: boolean = false;

  selectObserver: MutationObserver;

  /**
   * Returns the select element from the <slot />
   */
  get selectElement(): HTMLSelectElement {
    const { host } = this;
    const selectElement = host.querySelector('select');
    return selectElement;
  }

  /**
   * An array of CSS classes that may have been on this.selectElement in the <slot /> when
   * this component was first rendered.
   */
  private classListInitial: string[] = [];

  /**
   * The id of the select element
   * might be hard-coded in the <select /> tag or generated if not hard-coded
   */
  private selectElementId: string = `hp-text-select-id-${uid()}`;

  /**
   * Return value of window.setTimeout()
   */
  private resizeTimeout: number;

  /**
   * handleWindowResize()
   */
  @Listen('resize', { target: 'window' })
  handleWindowResize(): void {
    this.onWindowResize();
  }

  /**
   * onWindowResize()
   */
  onWindowResize(): void {
    this.resizeSelect();
  }

  /**
   * labelSizeSelectionChanged()
   *
   * @param newValue
   * @param oldValue
   */
  @Watch('labelSizeSelection')
  labelSizeSelectionChanged(newValue: boolean = undefined, oldValue: boolean = undefined) {
    if (newValue !== oldValue) {
      this.resizeSelect();
    }
  }

  @Watch('selectedIndex')
  onSelectedIndexChange() {
    const { selectedIndex } = this;
    const options = this.getSelectOptions();

    if (selectedIndex >= options.length) {
      warn(
        `hp6-text-select -- not setting selectedIndex prop ${selectedIndex} is greater than options.length`,
      );
      return;
    }

    if (selectedIndex < 0) {
      warn(
        `hp6-text-select -- not setting selectedIndex prop to negative number ${selectedIndex} `,
      );
      return;
    }

    const { selectElement } = this;
    selectElement.selectedIndex = selectedIndex;

    this.resizeSelect();
  }

  async measureSelectedOption(): Promise<number> {
    const { selectedIndex } = this;
    const options = this.getSelectOptions();
    if (!options || options.length === 0) {
      return 0;
    }

    return new Promise(resolve => {
      const hiddenDiv = this.host.querySelector('.option-measure');

      const option = options[selectedIndex];

      const innerHTML = `${option.label}&nbsp;`;
      hiddenDiv.innerHTML = innerHTML;
      const optionWidth = hiddenDiv.clientWidth;

      hiddenDiv.innerHTML = ''; // clear the hidden div

      if (optionWidth) {
        resolve(optionWidth);
        return;
      }

      // If the option width is 0, recursively wait for the browser to render the option width
      const handle = requestAnimationFrame(async () => {
        const optionWidth = await this.measureSelectedOption();
        resolve(optionWidth);
        cancelAnimationFrame(handle);
      });
    });
  }

  /**
   * resizeSelect()
   */
  async resizeSelect(): Promise<void> {
    const { selectElement, labelSizeSelection } = this;

    const label = this.host.querySelector('label');

    if (!label) {
      warn('hp6-text-select - label element not found. label-text prop is required');
      return;
    }

    // remove all classes from the select
    selectElement.className = '';

    // re-add the initial classes onto selectElement
    this.classListInitial.forEach(c => {
      selectElement.classList.add(c);
    });

    selectElement.classList.add('hp6-text-select-element');
    selectElement.classList.add(labelSizeSelection);

    const selectedOptionWidth = await this.measureSelectedOption();
    const labelWidth = Math.round(label.getBoundingClientRect().width);

    // resize the entire <select /> width
    this.host.style.setProperty('--option-width', `${selectedOptionWidth}px`);
    this.host.style.setProperty('--label-width', `${labelWidth}px`);

    this.isLoaded = true;
  }

  /**
   * onChange()
   */
  private onChange(): void {
    const { selectElement } = this;
    const { selectedIndex } = selectElement;
    this.selectedIndex = selectedIndex;
  }

  /**
   * getSelectOptions()
   *
   * TODO: determine a way to prevent allowing "multiple" in the <select /> - implement same in <hp6-select />
   *
   * @returns HTMLOptionsCollection
   */
  private getSelectOptions() {
    const { selectElement } = this;
    const options = Array.from(selectElement.querySelectorAll('option'));
    if (!options || options.length === 0) {
      warn('hp6-text-select - no options found in select element');
      return [];
    }
    return options;
  }

  /**
   * Stencil lifecycle
   */
  disconnectedCallback() {
    const { selectElement, resizeTimeout } = this;
    selectElement.removeEventListener('change', this.onChange);
    clearTimeout(resizeTimeout);

    if (this.selectObserver) {
      this.selectObserver.disconnect();
      this.selectObserver = null;
    }
    this.resizeTimeout = null;
  }

  /**
   * Stencil lifecycle
   */
  componentWillLoad() {
    const { selectElement, labelSizeSelection } = this;

    // assign an id to the <select /> if one was not hard-coded
    const { id } = selectElement;
    let selectElementId = id;

    if (!selectElementId) {
      selectElementId = this.selectElementId;
    }

    selectElement.id = selectElementId;
    this.selectElementId = selectElementId;

    const { classList } = selectElement;
    this.classListInitial = Array.from(classList);

    selectElement.classList.add('hp6-text-select-element');
    selectElement.classList.add(labelSizeSelection);
    selectElement.addEventListener('change', this.onChange);

    const { selectedIndex } = this;
    selectElement.selectedIndex = selectedIndex || 0;
  }

  componentDidLoad() {
    this.resizeSelect();

    if (Build.isBrowser) {
      this.selectObserver = new MutationObserver(() => {
        this.resizeSelect();
      });
      this.selectObserver.observe(this.selectElement, {
        childList: true,
        subtree: true,
      });
    }
  }

  componentDidUpdate() {
    this.resizeSelect();
  }

  /**
   * constructor()
   */
  constructor() {
    this.getSelectOptions = this.getSelectOptions.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onWindowResize = debounce(this.onWindowResize.bind(this), 33); // 30fps
    this.onSelectedIndexChange = this.onSelectedIndexChange.bind(this);
  }

  /**
   * get iconSize()
   */
  get iconSize() {
    const { labelSizeSelection } = this;
    return labelSizeSelection === 'md' ? 'sm' : 'xs';
  }

  /**
   * render()
   *
   * @returns tsx
   */
  render() {
    const { selectElementId, labelText, labelSizeSelection, iconSize, isLoaded } = this;

    if (!labelText) {
      warn('hp6-text-select - labelText attribute required');
      return null;
    }

    return (
      <Host class={isLoaded ? 'loaded' : ''}>
        <hp6-label-decorator
          size-selection={labelSizeSelection}
          type-selection="heading"
          show-field-optional="false"
        >
          <label htmlFor={selectElementId}>{labelText}</label>
        </hp6-label-decorator>
        <slot />
        <hp6-icon name="system-arrow-down" size-selection={iconSize} />
        <div class={`hp6-text-select-element option-measure ${labelSizeSelection}`} />
      </Host>
    );
  }
}
